import type { ProFormInstance } from '@ant-design/pro-components';
import { ProForm, ProFormText } from '@ant-design/pro-components';
import { Button, Drawer, Space } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import useAppMessage from '@/hooks/useAppMessage';
import { changePassword } from '@/services/user';
import {
  createConfirmPasswordRules,
  createPasswordRules,
} from '@/utils/password';

export interface ChangePasswordDrawerProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordDrawer: React.FC<ChangePasswordDrawerProps> = ({
  open,
  onClose,
}) => {
  const appMessage = useAppMessage();
  const formRef = useRef<ProFormInstance | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      formRef.current?.resetFields();
    }
  }, [open]);

  const handleSubmit = async () => {
    try {
      const values = await formRef.current?.validateFields();
      if (!values) {
        return;
      }

      setLoading(true);
      await changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      appMessage.success('密码已修改');
      onClose();
    } catch (error: unknown) {
      console.error('密码修改失败:', error);
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title="修改密码"
      size={600}
      open={open}
      onClose={onClose}
      closable={{ placement: 'end' }}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            保存
          </Button>
        </Space>
      }
    >
      <ProForm formRef={formRef} submitter={false} layout="vertical">
        <ProFormText.Password
          name="oldPassword"
          label="旧密码"
          rules={[{ required: true, message: '请输入旧密码' }]}
          fieldProps={{
            placeholder: '请输入旧密码',
          }}
        />
        <ProFormText.Password
          name="newPassword"
          label="新密码"
          rules={createPasswordRules({ requiredMessage: '请输入新密码' })}
          fieldProps={{
            placeholder: '请输入新密码',
          }}
        />
        <ProFormText.Password
          name="confirmPassword"
          label="确认密码"
          rules={createConfirmPasswordRules({ matchField: 'newPassword' })}
          fieldProps={{
            placeholder: '请再次输入新密码',
          }}
        />
      </ProForm>
    </Drawer>
  );
};

export default ChangePasswordDrawer;
